import { fromEnumAsString, toEnumFromStringOrNull } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { isNotNullOrUndefined } from '../../utility/null-safety.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, SerializationContext, DataModelBuilder,
  SerializedData, cloneMap, isSameMap, mapToData } from '../dto/dto.js';
import { StaffPermission } from './staff-permission.js';

export class StaffRole extends DataModel {
  static readonly TYPE_ID: string = 'c2815f564ef44001b325bb632bdb29bc';
  static readonly SCHEMA_VERSION: number = 2;

  static readonly BASIC: string = 'cf228795c90243f99f41a9b8724f3a85';
  static readonly STAFF: string = 'cf03d42a715443858c0fa6f45a721da4';
  static readonly MANAGER: string = '68aa198be45e426db12b48072b8970cc';
  static readonly NONE: string = '__STAFF_ROLE_NONE__';
  static readonly ADMINISTRATOR: string = '__STAFF_ROLE_ADMINISTRATOR__';
  static readonly DEVELOPER: string = '__STAFF_ROLE_DEVELOPER__';

  static readonly kMaxPermissions = 9;

  constructor(
    public readonly uniqueId: string,
    public readonly permissions: Map<StaffPermission, boolean>,
  ) {
    super();
  }

  get $typeId(): string {
    return StaffRole.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, StaffRole.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): StaffRole {
    const rawPermissions = MapUtils.getMap<StaffPermission | null, boolean>(
        map,
        'permissions',
        (k: string, v: unknown) => {
          return {
            key: toEnumFromStringOrNull(k, StaffPermission),
            value: v as boolean,
          };
        });

    const permissions = new Map<StaffPermission, boolean>();
    rawPermissions.forEach((value: boolean, key: StaffPermission | null) => {
      if(isNotNullOrUndefined(key)) {
        permissions.set(key, value);
      }
    });

    return new StaffRole(
        MapUtils.get<string>(map, 'uniqueId'),
        permissions,
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'permissions': mapToData(this.permissions, (k: StaffPermission, v: boolean) => {
        return {
          key: fromEnumAsString(k),
          value: v,
        };
      }),
    };
  }

  clone(): this {
    return new StaffRole(
        this.uniqueId,
        cloneMap(this.permissions),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        isSameMap(other.permissions, this.permissions);
  }
}

export class StaffRoleBuilder extends DataModelBuilder<StaffRole> {
  build(context: SerializationContext, map: SerializedData): StaffRole {
    return StaffRole.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(StaffRole.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(StaffRole.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;
    if(version < 2) {
      const uniqueId = MapUtils.get<string>(map, 'uniqueId');

      if(uniqueId == StaffRole.MANAGER || uniqueId == StaffRole.ADMINISTRATOR || uniqueId == StaffRole.DEVELOPER) {
        const rawPermissions = MapUtils.getRaw(map, 'permissions', {});

        MapUtils.set(map, fromEnumAsString(StaffPermission.CanEditOtherStaffSchedule), true);
        MapUtils.set(map, 'permissions', rawPermissions);
      }

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
